import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority"

import { cn } from "../../lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium font-ui transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary-glow hover:shadow-gold",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-border bg-transparent text-foreground hover:bg-card hover:border-primary/50 hover:text-primary",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: 
          "text-foreground hover:bg-card hover:text-primary",
        link: 
          "text-primary underline-offset-4 hover:underline",
        // Mystical variants
        mystical:
          "bg-mystical text-mystical-foreground hover:bg-mystical-glow hover:shadow-mystical",
        accent:
          "bg-accent text-accent-foreground hover:bg-accent-glow",
        emerald:
          "bg-emerald text-emerald-foreground hover:opacity-90",
        // Special variants for the course
        gold:
          "bg-gradient-to-r from-primary to-primary-glow text-primary-foreground font-semibold hover:shadow-gold hover:scale-[1.02]",
        complete:
          "bg-success text-success-foreground hover:bg-success/90",
        codex:
          "border border-primary/30 bg-card/50 text-foreground hover:border-primary hover:bg-card hover:text-primary backdrop-blur-sm",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8 text-base",
        xl: "h-12 rounded-lg px-10 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    />
  )
})
Button.displayName = "Button"

export { Button, buttonVariants }
